import { appGetState } from 'legacy/lut/store'
import { useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { isAttentionSeverityTop } from 'partner360/entities/attentionStatuses'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from 'partner360/features/acknowledgmentStatusDialog'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { useAttentionDetailSeverity } from '../../hooks/useAttentionDetailSeverity'
import type { IAttentionReminder } from '../../hooks/useAttentionReminder'
import type { IAttentionStatusesReminder } from '../../types'
import { getStepInitials } from '../attentionNextSteps/getStepInitials'

import styles from './SobPbmList.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses
const NEXT_KEYS = KEYS.nextSteps

interface ISobPbmListProps {
  pbms: IAttentionStatusesReminder[]
  /** The partner's SOB reminder; it says who was reminded today and sends. */
  reminder: IAttentionReminder
  /** The reader may remind them; a PBM reads who else is on the partner and reminds nobody. */
  canRemind: boolean
}

/**
 * Who the SOB figures wait for: the partner's PBMs on the offering, each with their own reminder
 * and `Remind all` where there are several. Once reminded today, a PBM's button says so and stays
 * off; the service decides, so it stays off across sessions.
 */
export const SobPbmList = ({ pbms, reminder, canRemind }: ISobPbmListProps) => {
  const { remind, stateOf } = reminder
  const severity = useAttentionDetailSeverity()
  const remindColor = severity !== null && isAttentionSeverityTop(severity) ? 'danger' : 'warning'
  const currentUserEmail = useMemo(() => (appGetState().user.email ?? '').toLowerCase(), [])

  if (pbms.length === 0) {
    return null
  }

  const isAllReminded = pbms.every(({ email }) => stateOf(email) === 'remindedToday')

  return (
    <div className={styles.root}>
      <span className={styles.heading}>
        <Translation span text={pbms.length > 1 ? KEYS.units.pbm.many : KEYS.units.pbm.one} />
      </span>

      <ul className={styles.list}>
        {pbms.map((pbm) => {
          const state = stateOf(pbm.email)
          const isSent = state === 'remindedToday'

          return (
            <li key={pbm.email} className={styles.row}>
              <span className={styles.avatar} aria-hidden="true">
                {getStepInitials(pbm.name)}
              </span>
              <span className={styles.person}>
                <span className={styles.name}>
                  {pbm.name}
                  {pbm.email.toLowerCase() === currentUserEmail && (
                    <span className={styles.you}>
                      <Translation
                        span
                        text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.you}
                      />
                    </span>
                  )}
                </span>
                {pbm.name !== pbm.email && <span className={styles.email}>{pbm.email}</span>}
              </span>
              {canRemind && (
                <Button
                  className={styles.remind}
                  variant="contained"
                  color={remindColor}
                  size="sm"
                  icon={isSent ? 'kbase kbase-check' : 'kbase kbase-bell'}
                  disabled={isSent || state === 'blocked' || state === 'pending'}
                  onClick={() => void remind([pbm])}
                >
                  <Translation span text={isSent ? NEXT_KEYS.remindedToday : NEXT_KEYS.remind} />
                </Button>
              )}
            </li>
          )
        })}
      </ul>

      {canRemind && pbms.length > 1 && (
        <Button
          className={styles.remindAll}
          variant="text"
          size="sm"
          icon="kbase kbase-bell"
          disabled={isAllReminded}
          onClick={() => void remind(pbms)}
        >
          <Translation span text={KEYS.reminder.remindAll} />
        </Button>
      )}
    </div>
  )
}

SobPbmList.displayName = 'SobPbmList'
